import { jwtDecode } from "jwt-decode";

// =============================================================================
// Permissões no frontend.
//
// Um modelo só: a chave do cargo (RBAC), no formato "modulo.recurso.acao", vinda
// de `/api/me/access`. Quem é da equipe da plataforma (POWER_USER) não precisa de
// chave — passa em tudo. O tipo legado (ADMIN/USER) não decide mais nada aqui.
//
// O gate de verdade é sempre o do servidor — o que está aqui decide o que
// DESENHAR, nunca o que é permitido.
// =============================================================================

/** Equipe da plataforma: `access.permissionId` vem de `/api/me/access`. */
export const isPlatformStaff = (access) =>
  String(access?.permissionId || "").toUpperCase() === "POWER_USER";

// -----------------------------------------------------------------------------
// Features em teste: chaves `forTester` [USR, 2026-11-03]
//
// Uma feature publicada para teste em produção tem uma chave marcada como
// `forTester` no catálogo do backend. Ela só vale para quem tem a marca de
// tester no usuário e para a equipe da plataforma: o "*" do cargo NÃO a cobre
// (o cargo Administrador não vê feature em teste), e — diferente das outras
// chaves — "ainda não sei" vale NÃO, para a feature não aparecer por um
// instante a quem não é tester enquanto `/me/access` carrega. A lista vem de
// `/me/access` (`forTesterKeys`) e fica guardada no navegador para valer já no
// próximo carregamento. Na tela, a feature usa `can(chave)` como qualquer outra.
// -----------------------------------------------------------------------------
const FOR_TESTER_STORAGE_KEY = "relinq.forTesterKeys";

const readStoredForTesterKeys = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(FOR_TESTER_STORAGE_KEY));
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

let storedForTesterKeys = readStoredForTesterKeys();

/** Guarda a lista de chaves `forTester` recebida de `/me/access`. */
export const rememberForTesterKeys = (keys) => {
  if (!Array.isArray(keys)) return;
  storedForTesterKeys = keys.map(String);
  try {
    localStorage.setItem(
      FOR_TESTER_STORAGE_KEY,
      JSON.stringify(storedForTesterKeys),
    );
  } catch {
    // Armazenamento indisponível: a lista vale só nesta sessão.
  }
};

/** A chave é de uma feature em teste? */
export const isForTesterKey = (access, permissionKey) => {
  const keys = Array.isArray(access?.forTesterKeys)
    ? access.forTesterKeys
    : storedForTesterKeys;
  return keys.includes(permissionKey);
};

/** O usuário logado tem a marca de tester (`/me/access`)? */
export const isTesterUser = (access) => access?.tester === true;

/**
 * `access` é o objeto de /api/me/access ({ permissions, dataScope, ... }).
 *
 * Enquanto ele não chegou (primeiro render), devolvemos `true`: esconder o
 * menu inteiro por meio segundo a cada carregamento é pior do que mostrar um
 * item que o servidor vai recusar depois. A recusa real acontece no backend.
 * Exceção: chave de feature em teste (`forTester`), ver acima.
 */
export const hasPermission = (access, permissionKey) => {
  if (!permissionKey) return true;
  if (isForTesterKey(access, permissionKey)) {
    if (!access) return false;
    if (isPlatformStaff(access)) return true;
    return (
      Array.isArray(access.permissions) &&
      access.permissions.includes(permissionKey)
    );
  }
  if (!access) return true;
  if (isPlatformStaff(access)) return true;

  const list = Array.isArray(access.permissions) ? access.permissions : null;
  if (!list) return true;

  return list.includes("*") || list.includes(permissionKey);
};

export const hasAnyPermission = (access, keys = []) =>
  (Array.isArray(keys) ? keys : [keys]).some((key) => hasPermission(access, key));

export const hasAllPermissions = (access, keys = []) =>
  (Array.isArray(keys) ? keys : [keys]).every((key) => hasPermission(access, key));

/** Chave que separa "vê os dados de toda a empresa" de "vê só os próprios". */
export const VIEW_ALL_PERMISSION = "data.viewAll";

/**
 * Diferente de `hasPermission`, aqui "ainda não sei" vale NÃO: as telas decidem
 * com isto o que pedir ao servidor (ex.: "Todos" × "só os meus"), e começar
 * pedindo demais seria pior do que começar restrito.
 */
export const canViewAllData = (access) =>
  Boolean(access) && hasPermission(access, VIEW_ALL_PERMISSION);

/**
 * Mapa "chave → chaves que ela já inclui", montado a partir do catálogo de
 * `GET /roles/catalog` (campo `implies` de cada permissão). A fonte da regra é o
 * backend (`IMPLIED_PERMISSIONS`); aqui só se desenha o que ele já aplica.
 */
export const buildImpliedMap = (catalog) => {
  const map = {};
  (catalog?.groups || []).forEach((group) =>
    (group.permissions || []).forEach((permission) => {
      if (Array.isArray(permission.implies) && permission.implies.length) {
        map[permission.key] = permission.implies;
      }
    }),
  );
  return map;
};

/** Acrescenta à lista tudo o que as chaves dela já incluem (fecho transitivo). */
export const expandImpliedKeys = (keys, impliedMap = {}) => {
  const list = Array.isArray(keys) ? keys : [];
  if (list.includes("*")) return ["*"];
  const seen = new Set(list);
  const queue = [...list];
  while (queue.length) {
    const key = queue.shift();
    (impliedMap[key] || []).forEach((implied) => {
      if (!seen.has(implied)) {
        seen.add(implied);
        queue.push(implied);
      }
    });
  }
  return Array.from(seen);
};

/** "own" | "team" | "company" — usado por telas que mostram filtro de escopo. */
export const dataScopeOf = (access) => access?.dataScope || "company";

export const getUserId = () => {
  try {
    const token = localStorage.getItem("token");
    const { user } = jwtDecode(token);

    return user.id;
  } catch (error) {
    return 0;
  }
};

export const removeToken = () => {
  localStorage.removeItem("token");
  window.location.href = "/login";
};
